import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, IconButton, Tab, Tabs, TextField, Tooltip, Typography } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import AsyncSearch from 'components/AsyncSearch';
import useDebounce from 'hooks/useDebounce';
import moment from 'moment';
import Excel from 'assets/icons/svg/Excel';
import {
  getPartnerRatingAreas,
  getPartnerRatingPartners,
  getPartnerRatingRealEstates,
  getPartnerRatingServiceOrgs,
} from '../../api';
import { IArea, IPartner, IRealEstate, IServiceOrg } from '../../interfaces';
import { Points, Recommendations } from '../../components';
import {
  useGetPartnersRatingByPartnerUgo,
  useGetNomenclatures,
  useExportAllPartnersRatingByPartnerUgoExcelReport,
} from './hooks';
import { PartnersRatingReport } from './components';
import { INomenclature } from './interfaces';

const ByPartnersUgo = () => {
  // Local state
  const [selectedNomenclature, setSelectedNomenclature] = useState<INomenclature | null>(null);
  const [selectedServiceOrgs, setServiceOrgs] = useState<IServiceOrg[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<IArea[]>([]);
  const [selectedRealEstates, setSelectedRealEstates] = useState<IRealEstate[]>([]);
  const [selectedPartners, setSelectedPartners] = useState<IPartner[]>([]);
  const [selectedYear, setSelectedYear] = useState(() => moment());
  const [pageNo, setPageNo] = useState(1);
  const pageSize = 200;

  const debouncedServiceOrgs = useDebounce(selectedServiceOrgs);
  const debouncedAreas = useDebounce(selectedAreas);
  const debouncedRealEstates = useDebounce(selectedRealEstates);
  const debouncedPartners = useDebounce(selectedPartners);
  const debouncedYear = useDebounce(selectedYear);

  const { fetchPartnersRatingByPartnerUgo, ...partnersRatingState } = useGetPartnersRatingByPartnerUgo();
  const { exportAllPartnersRatingByPartnerUgoExcelReport, ...allPartnersRatingByPartnerExcelState } =
    useExportAllPartnersRatingByPartnerUgoExcelReport();
  const nomenclaturesState = useGetNomenclatures((nomenclatures) => setSelectedNomenclature(nomenclatures[0]));

  const params = {
    pageNo,
    pageSize,
    serviceOrgGuids: debouncedServiceOrgs.map((serviceOrg) => serviceOrg.serviceOrgGuid),
    areaIds: debouncedAreas.map((area) => area.areaId),
    realEstateGuids: debouncedRealEstates.map((realEstate) => realEstate.realEstateGuid),
    partnerGuids: debouncedPartners.map((partner) => partner.partnerGuid),
    nomenclaturaGuid: selectedNomenclature?.nomenclaturaGuid,
    year: selectedYear ? moment(debouncedYear).get('year') : null,
  };
  const mapKey = JSON.stringify(
    Object.entries(params)
      .map(([, value]) => value)
      .filter(Boolean),
  );

  function handleResetFilters() {
    setServiceOrgs([]);
    setSelectedAreas([]);
    setSelectedRealEstates([]);
    setSelectedPartners([]);
    setSelectedYear(moment());
  }

  useEffect(() => {
    if (!selectedNomenclature) return;

    fetchPartnersRatingByPartnerUgo({ params });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageNo,
    debouncedServiceOrgs,
    debouncedAreas,
    debouncedRealEstates,
    debouncedPartners,
    selectedNomenclature,
    debouncedYear,
  ]);

  const partnersRating = partnersRatingState.data.get(mapKey) || { list: [], totalCount: 0 };

  if (!selectedNomenclature) return null;

  return (
    <Box sx={{ bgcolor: '#fff', borderRadius: 20 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', p: 3, mb: 3, gridGap: 24 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gridGap: 8 }}>
          <Typography component="h2" variant="h5">
            <b>Рейтинг партнеров</b>
          </Typography>

          <Points />
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedPartners}
              fetch={getPartnerRatingPartners}
              remoteSearch
              uniqueBy="partnerGuid"
              matchKeys={['partnerName']}
              getOptionLabel={(option) => option.partnerName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setSelectedPartners(options)}
              renderInput={(props) => <TextField {...props} label="Партнеры" variant="filled" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedServiceOrgs}
              fetch={getPartnerRatingServiceOrgs}
              uniqueBy="serviceOrgGuid"
              matchKeys={['currentName']}
              getOptionLabel={(option) => option.currentName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setServiceOrgs(options)}
              renderInput={(props) => <TextField {...props} label="Дирекции" variant="filled" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedAreas}
              fetch={getPartnerRatingAreas}
              config={{
                params: { serviceOrgGuids: selectedServiceOrgs.map((serviceOrg) => serviceOrg.serviceOrgGuid) },
              }}
              uniqueBy="areaId"
              matchKeys={['areaName']}
              getOptionLabel={(option) => option.areaName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setSelectedAreas(options)}
              renderInput={(props) => <TextField {...props} label="Участки" variant="filled" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedRealEstates}
              fetch={getPartnerRatingRealEstates}
              remoteSearch
              config={{ params: { areaIds: selectedAreas.map((area) => area.areaId) } }}
              uniqueBy="realEstateGuid"
              matchKeys={['realEstateName']}
              getOptionLabel={(option) => option.realEstateName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setSelectedRealEstates(options)}
              renderInput={(props) => (
                <TextField {...props} label="Объекты недвижимости" variant="filled" size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <DatePicker
              variant="inline"
              inputVariant="filled"
              size="small"
              label="Год"
              format="yyyy"
              openTo="year"
              views={['year']}
              value={selectedYear}
              disabled={partnersRatingState.loading}
              onChange={(date) => date && setSelectedYear(moment(date))}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={1}>
            <Button variant="outlined" onClick={handleResetFilters}>
              Сбросить
            </Button>

            <Tooltip title="Полная выгрузка рейтинга партнеров УГО">
              <span>
                <IconButton
                  aria-label="Скачать отчет в Excel"
                  onClick={() => exportAllPartnersRatingByPartnerUgoExcelReport({ params })}
                  disabled={
                    !partnersRating.totalCount ||
                    partnersRatingState.loading ||
                    allPartnersRatingByPartnerExcelState.loading
                  }
                >
                  <Excel style={{ color: '#3C763D' }} />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>

        <Recommendations />

        <Box sx={{ display: 'flex', alignItems: 'center', gridGap: 16 }}>
          <Tabs
            value={selectedNomenclature?.nomenclaturaGuid}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {nomenclaturesState.data.map((nomenclature) => (
              <Tab
                key={nomenclature.nomenclaturaGuid}
                label={nomenclature.nomenclaturaName}
                value={nomenclature.nomenclaturaGuid}
                onClick={() => setSelectedNomenclature(nomenclature)}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      <PartnersRatingReport
        {...partnersRatingState}
        data={partnersRating}
        page={pageNo}
        pageSize={pageSize}
        nomenclature={selectedNomenclature!}
        year={selectedYear.get('year')}
        onChangePage={(page) => setPageNo(page)}
      />
    </Box>
  );
};

export default ByPartnersUgo;
